import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../../services/api';
import styles from './PrivacyData.module.scss';

/**
 * "Privacy & data" panel for the customer profile — the data-subject rights required by
 * GDPR / UAE PDPL / India DPDP:
 *   • Download my data  → GET /customers/me/data-export (right of access / portability)
 *   • Delete my account → POST /customers/me/delete-account (right to erasure, by anonymisation)
 */
const PrivacyData: React.FC = () => {
  const navigate = useNavigate();
  const [exporting, setExporting] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [verifying, setVerifying] = useState(false);
  const [verifyMsg, setVerifyMsg] = useState<string | null>(null);

  const sendVerify = async () => {
    setError(null); setVerifyMsg(null); setVerifying(true);
    try {
      const res = await apiService.sendEmailVerification();
      setVerifyMsg(res?.message || 'Verification email sent.');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not send verification email.');
    } finally {
      setVerifying(false);
    }
  };

  const download = async () => {
    setError(null);
    setExporting(true);
    try {
      const data = await apiService.exportMyData();
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `chatup24-my-data-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not prepare your data. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  const confirmDelete = async () => {
    setError(null);
    setDeleting(true);
    try {
      await apiService.deleteMyAccount();
      try { await apiService.logout(); } catch { /* best-effort */ }
      try { apiService.clearTokens(); } catch { /* best-effort */ }
      navigate('/', { replace: true });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not delete your account. Please try again.');
      setDeleting(false);
    }
  };

  return (
    <div className={styles.formSection}>
      <label className={styles.sectionLabel}>Privacy &amp; data</label>
      <p className={styles.intro}>
        You control the personal data we hold about you. Download a copy at any time, or permanently
        delete your account.
      </p>

      {error && <div className={styles.error} role="alert">{error}</div>}

      <div className={styles.row}>
        <div className={styles.rowText}>
          <div className={styles.rowTitle}>Verify my email</div>
          <div className={styles.rowDesc}>{verifyMsg || 'Confirm your email address so we know we can reach the real you.'}</div>
        </div>
        <button type="button" className={styles.secondaryBtn} onClick={sendVerify} disabled={verifying}>
          {verifying ? 'Sending…' : 'Send link'}
        </button>
      </div>

      <div className={styles.row}>
        <div className={styles.rowText}>
          <div className={styles.rowTitle}>Download my data</div>
          <div className={styles.rowDesc}>Get a JSON copy of your profile, bookings, appointments and reviews.</div>
        </div>
        <button type="button" className={styles.secondaryBtn} onClick={download} disabled={exporting}>
          {exporting ? 'Preparing…' : 'Download'}
        </button>
      </div>

      <div className={`${styles.row} ${styles.danger}`}>
        <div className={styles.rowText}>
          <div className={styles.rowTitle}>Delete my account</div>
          <div className={styles.rowDesc}>
            Removes your personal details and signs you out for good. Past transactions are kept in
            anonymised form where the law requires it. This cannot be undone.
          </div>
        </div>
        <button type="button" className={styles.dangerBtn} onClick={() => { setConfirmOpen(true); setConfirmText(''); }}>
          Delete account
        </button>
      </div>

      {confirmOpen && (
        <div className={styles.modalBackdrop} role="dialog" aria-modal="true" aria-label="Confirm account deletion">
          <div className={styles.modal}>
            <h3 className={styles.modalTitle}>Delete your account?</h3>
            <p className={styles.modalText}>
              This permanently removes your name, phone, photo and profile. You won’t be able to sign
              back in with this account. To confirm, type <strong>DELETE</strong> below.
            </p>
            <input
              type="text"
              className={styles.confirmInput}
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder="Type DELETE"
              aria-label="Type DELETE to confirm"
            />
            <div className={styles.modalActions}>
              <button
                type="button"
                className={styles.secondaryBtn}
                onClick={() => setConfirmOpen(false)}
                disabled={deleting}
              >
                Cancel
              </button>
              <button
                type="button"
                className={styles.dangerBtn}
                onClick={confirmDelete}
                disabled={deleting || confirmText.trim().toUpperCase() !== 'DELETE'}
              >
                {deleting ? 'Deleting…' : 'Permanently delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PrivacyData;
